import React, { useEffect, useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Clock, ArrowRight, Menu } from 'lucide-react';
import './QuizAttempt.css';
import logo from '../assets/logo.jpg';

export const QuizAttempt = () => {
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [attemptData, setAttemptData] = useState(null);
    const [userId, setUserId] = useState(null);
    const [timeLeft, setTimeLeft] = useState(null);
    const [totalQuestionCount, setTotalQuestionCount] = useState(0);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    // New state to indicate that quiz submission is in progress
    const [isSubmitting, setIsSubmitting] = useState(false);
    // UI: Remaining attempts dialog
    const [showAttemptsDialog, setShowAttemptsDialog] = useState(false);
    const [remainingAttempts, setRemainingAttempts] = useState(0);
    // Track tab-switch events and prevent double counting per hide
    const [showTabSwitchDialog, setShowTabSwitchDialog] = useState(false);
    const [countedThisHide, setCountedThisHide] = useState(false);
    const location = useLocation();

    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showConfirmEndDialog, setShowConfirmEndDialog] = useState(false);
    const [confirmEndMessage, setConfirmEndMessage] = useState('');

    useEffect(() => {
        // Check if coming from quiz selection with valid state
        if (!location.state?.fromQuizSelection) {
            navigate('/quiz-selection');
        }
    }, [location, navigate]);
    // Helper function to shuffle array
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    useEffect(() => {
        loadQuizData();
    }, []);

    useEffect(() => {
        let timer;
        if (timeLeft !== null && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        // When time runs out, end the quiz (if not already submitting)
                        if (!isSubmitting) handleEndQuiz();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [timeLeft, isSubmitting]);

    const loadQuizData = async () => {
        try {
            setLoading(true);
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError) throw userError;
            if (!user) {
                navigate('/login');
                return;
            }
            setUserId(user.id);

            // Check for existing incomplete attempt
            const { data: existingAttempts, error: attemptError } = await supabase
                .from('quiz_attempts')
                .select('*')
                .eq('user_id', user.id)
                .eq('is_completed', false)
                .order('created_at', { ascending: false })
                .limit(1);

            if (attemptError) throw attemptError;

            let attempt = existingAttempts?.[0];
            let isNewAttempt = false;

            // Handle existing attempt
            if (attempt) {
                const newResumeCount = (attempt.resume_count || 0) + 1;
                // Update resume count in DB
                const { error: updateError } = await supabase
                    .from('quiz_attempts')
                    .update({ resume_count: newResumeCount })
                    .eq('id', attempt.id);

                if (updateError) throw updateError;

                // Check if exceeded max attempts
                if (newResumeCount >= 4) {
                    setIsSubmitting(true); // Show submitting message
                    await handleEndQuizAutomatically(attempt.id);
                    navigate('/result', {
                        state: {
                            message: 'You have used all 3 attempts.You will get the report on mail after 24-48 business hours.',
                            fromQuiz: true // Ensure ResultPage doesn't redirect
                        }
                    });
                    return;
                } else {
                    // Show remaining attempts in dialog (no alerts)
                    const remaining = 4 - newResumeCount;
                    setRemainingAttempts(remaining);
                    setShowAttemptsDialog(true);
                }
                attempt.resume_count = newResumeCount;
            } else {
                // No existing incomplete attempt: don't create a new DB row until the user answers a question.
                attempt = null;
                isNewAttempt = true;
            }

            setAttemptData(attempt);

            // Load questions
            const { data: questionData, error: questionError } = await supabase
                .from('quizzes')
                .select('*');

            if (questionError) throw questionError;

            // For a NEW attempt, exclude questions previously answered in any completed attempts by this user
            let baseQuestions = questionData;
            if (isNewAttempt) {
                try {
                    const { data: completedAttempts, error: completedErr } = await supabase
                        .from('quiz_attempts')
                        .select('responses')
                        .eq('user_id', user.id)
                        .eq('is_completed', true);
                    if (completedErr) throw completedErr;

                    const attemptedIds = new Set();
                    (completedAttempts || []).forEach(att => {
                        (att.responses || []).forEach(r => {
                            if (r && typeof r.ques_id !== 'undefined' && r.ques_id !== null) {
                                attemptedIds.add(r.ques_id);
                            }
                        });
                    });

                    const filtered = baseQuestions.filter(q => !attemptedIds.has(q.ques_id));
                    // If user has answered all previously, fallback to full set to avoid empty quiz
                    baseQuestions = filtered.length > 0 ? filtered : baseQuestions;
                } catch (e) {
                    console.warn('Could not filter previously attempted questions:', e);
                }
            }

            const shuffledQuestions = baseQuestions
                .map(question => ({
                    ...question,
                    answers: shuffleArray(question.answers)
                }))
                .sort(() => Math.random() - 0.5);

            // Store total count before filtering so progress reflects full quiz
            setTotalQuestionCount(shuffledQuestions.length);

            // If resuming an existing attempt, hide already-answered questions
            if (!isNewAttempt && attempt) {
                const answeredIds = new Set((attempt.responses || []).filter(Boolean).map(r => r.ques_id));
                const remainingQuestions = shuffledQuestions.filter(q => !answeredIds.has(q.ques_id));
                setQuestions(remainingQuestions);
                setCurrentIndex(0);
            } else {
                setQuestions(shuffledQuestions);
            }

            // Set initial time limit (no localStorage persistence)
            try {
                const { data: timeLimit, error: timeLimitError } = await supabase
                    .rpc('calculate_quiz_time_limit', { quiz_id: 1 });
                if (timeLimitError) throw timeLimitError;
                // If resuming, compute remaining time from created_at
                if (!isNewAttempt && attempt?.created_at) {
                    const startedAt = new Date(attempt.created_at).getTime();
                    const now = Date.now();
                    const elapsedSec = Math.floor((now - startedAt) / 1000);
                    const remaining = Math.max(0, Number(timeLimit) - elapsedSec);
                    setTimeLeft(remaining);
                } else {
                    setTimeLeft(Number(timeLimit));
                }
            } catch (timeError) {
                console.warn('Using local time calculation:', timeError);
                // Base on total question count for fairness
                const fallback = Math.ceil((shuffledQuestions.length || questions.length) / 2) * 60;
                // If resuming, subtract elapsed
                if (!isNewAttempt && attempt?.created_at) {
                    const startedAt = new Date(attempt.created_at).getTime();
                    const now = Date.now();
                    const elapsedSec = Math.floor((now - startedAt) / 1000);
                    setTimeLeft(Math.max(0, fallback - elapsedSec));
                } else {
                    setTimeLeft(fallback);
                }
            }
        } catch (error) {
            console.error('Error loading quiz:', error);
            setErrorMessage('Error loading quiz. Please try again.');
            setShowErrorDialog(true);
        } finally {
            setLoading(false);
        }
    };

    // Prevent accidental reload/close during an active attempt (client-side only)
    useEffect(() => {
        if (!attemptData || isSubmitting) return;
        const beforeUnloadHandler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', beforeUnloadHandler);
        return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
    }, [attemptData, isSubmitting]);

    // Count tab switching as an attempt: when tab is hidden, increment resume_count
    // Show reminder when user returns
    useEffect(() => {
        const handleVisibility = async () => {
            if (!attemptData || isSubmitting) return;
            // When user leaves the tab (hidden), count it once
            if (document.hidden && !countedThisHide) {
                try {
                    setCountedThisHide(true);
                    const current = attemptData.resume_count || 0;
                    const { data, error } = await supabase
                        .from('quiz_attempts')
                        .update({ resume_count: current + 1 })
                        .eq('id', attemptData.id)
                        .select('resume_count')
                        .single();
                    if (error) throw error;

                    const newCount = data?.resume_count ?? current + 1;
                    // Update local state
                    setAttemptData(prev => ({ ...prev, resume_count: newCount }));

                    if (newCount >= 4) {
                        // Auto submit if exceeded limit
                        setIsSubmitting(true);
                        await handleEndQuizAutomatically(attemptData.id);
                        navigate('/result', {
                            state: {
                                message: 'You have used all 3 attempts.You will get the report on mail after 24-48 business hours.',
                                fromQuiz: true
                            }
                        });
                    }
                } catch (e) {
                    console.error('Error counting tab switch:', e);
                }
            }

            // When user returns (visible), show dialog with remaining attempts and reset counter flag
            if (!document.hidden) {
                setCountedThisHide(false);
                if (attemptData && !isSubmitting) {
                    const remaining = 4 - ((attemptData.resume_count || 0));
                    if (remaining > 0) {
                        setRemainingAttempts(remaining);
                        setShowTabSwitchDialog(true);
                    }
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [attemptData, isSubmitting, countedThisHide, navigate]);

    const formatTimeLeft = () => {
        if (timeLeft === null) return '--:--';
        const minutes = Math.floor(timeLeft / 60);
        const seconds = timeLeft % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleAnswer = async (answer) => {
        // Prevent answering if the quiz is already being submitted
        if (isSubmitting) return;

        try {
            const currentQuestion = questions[currentIndex];

            // If the answer is the same as the one already saved, do nothing.
            const existingForQ = attemptData?.responses?.find(r => r && r.ques_id === currentQuestion.ques_id);
            if (existingForQ?.answer === answer) {
                return;
            }

            const selectedAnswer = currentQuestion.answers.find(a => a.option === answer);
            let currentAttemptData = attemptData;

            if (!currentAttemptData) {
                // Create attempt row on first answer
                const { data: newAttempt, error: createError } = await supabase
                    .from('quiz_attempts')
                    .insert([{
                        user_id: userId,
                        responses: [],
                        score: 0,
                        created_at: new Date().toISOString()
                    }])
                    .select()
                    .single();

                if (createError) throw createError;
                currentAttemptData = newAttempt;
                setAttemptData(newAttempt);
            }

            const newResponse = {
                ques_id: currentQuestion.ques_id,
                answer: answer,
                score: selectedAnswer.points,
                category: selectedAnswer.category,
                question_category: currentQuestion.category,
                sub_category: currentQuestion.sub_category,
                remarks: selectedAnswer.remarks,
                scw:selectedAnswer.scw,
                strength: selectedAnswer.strength,
                weakness: selectedAnswer.weakness
            };

            const updatedResponses = [...(currentAttemptData.responses || [])];
            const existingIdx = updatedResponses.findIndex(r => r && r.ques_id === currentQuestion.ques_id);
            if (existingIdx >= 0) {
                updatedResponses[existingIdx] = newResponse;
            } else {
                updatedResponses.push(newResponse);
            }

            const totalScore = updatedResponses.reduce((sum, resp) =>
                sum + (resp?.score || 0), 0
            );

            const { error: updateError } = await supabase
                .from('quiz_attempts')
                .update({
                    responses: updatedResponses,
                    score: totalScore
                })
                .eq('id', currentAttemptData.id);

            if (updateError) throw updateError;

            setAttemptData({
                ...currentAttemptData,
                responses: updatedResponses,
                score: totalScore
            });

            // After answering, remove this question from the in-memory list so it never reappears
            setQuestions(prevQs => {
                const remaining = prevQs.filter(q => q.ques_id !== currentQuestion.ques_id);
                // Adjust index to stay within bounds based on new remaining length
                setCurrentIndex(prevIdx => Math.min(prevIdx, Math.max(0, remaining.length - 1)));
                // If none remain, end the quiz
                if (remaining.length === 0) {
                    (async () => {
                        setIsSubmitting(true);
                        await handleEndQuiz(true);
                    })();
                }
                return remaining;
            });
        } catch (error) {
            console.error('Error saving answer:', error);
            setErrorMessage('Error saving your answer. Please try again.');
            setShowErrorDialog(true);
        }
    };

    const handleEndQuizAutomatically = async (attemptId) => {
        try {
            const endTime = new Date().toISOString();
            const { error } = await supabase
                .from('quiz_attempts')
                .update({
                    end_time: endTime,
                    is_completed: true
                })
                .eq('id', attemptId);

            if (error) throw error;

        } catch (error) {
            console.error('Error auto-submitting quiz:', error);
        }
    };

    const handleEndQuiz = async (isLastQuestion = false, forced = false) => {
        // Set the flag so that the UI freezes while processing.
        setIsSubmitting(true);
        try {
            if (!attemptData || !attemptData.responses || attemptData.responses.length === 0) {
                setErrorMessage('Please answer at least one question before ending the quiz.');
                setShowErrorDialog(true);
                setIsSubmitting(false);
                return;
            }

            const totalQuestions = questions.length;
            const currentIds = new Set(questions.map(q => q.ques_id));
            const answeredQuestions = (attemptData.responses || []).filter(r => r && currentIds.has(r.ques_id)).length;
            const unansweredQuestions = totalQuestions - answeredQuestions;

            if (!isLastQuestion && unansweredQuestions > 0 && !forced) {
                setConfirmEndMessage(`You have ${unansweredQuestions} unanswered questions. Are you sure you want to end the quiz?`);
                setShowConfirmEndDialog(true);
                setIsSubmitting(false);
                return;
            }

            const endTime = new Date().toISOString();

            const { error } = await supabase
                .from('quiz_attempts')
                .update({
                    end_time: endTime,
                    is_completed: true
                })
                .eq('id', attemptData.id);

            if (error) throw error;

            navigate('/result', {
                state: {
                    message: 'You will get the report on mail after 24-48 business hours.',
                    fromQuiz: true
                }
            });

        } catch (error) {
            console.error('Error ending quiz:', error);
            setErrorMessage('Error ending quiz. Please try again.');
            setShowErrorDialog(true);
            setIsSubmitting(false);
        }
    };

    // Removed localStorage-based saveProgress; progress is maintained server-side via responses and locally in state

    const handleLogout = async () => {
        try {
            await supabase.auth.signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const getProgressPercentage = () => {
        if (!attemptData?.responses) return 0;
        const answered = (attemptData.responses || []).filter(r => r).length;
        const total = totalQuestionCount || questions.length || 1;
        return Math.round((answered / total) * 100);
    };

    if (loading) {
        return <div className="loading-screen">Loading...</div>;
    }

    // Optionally, if submission is in progress, you could show a full-screen overlay.
    if (isSubmitting) {
        return <div className="loading-screen">Submitting Assessment... Please wait.</div>;
    }

    const currentQuestion = questions[currentIndex];

    if (!currentQuestion) {
        return <div>No questions available</div>;
    }

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };

    return (
        <div className="quiz-container">
            <header className="quiz-header">
                <div className="header-content">
                    <button onClick={toggleSidebar} className="menu-button">
                        <Menu className="menu-icon" />
                    </button>
                    <div className="quiz-logo-wrapper">
                        <img src={logo} alt="Quiz Logo" className="quiz-logo" />
                    </div>
                    <h1 className="assessment-title">TrueIn Assessment</h1>
                    <button onClick={handleLogout} className="logout-button">
                        <LogOut className="inline-block w-4 h-4 mr-2" />
                        Logout
                    </button>
                </div>
            </header>

            <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
                <button onClick={closeSidebar} className="close-sidebar-button">
                    <ArrowRight className="close-icon" />
                </button>
                <div className="sidebar-logo">
                    <img src={logo} alt="Quiz Logo"  />
                </div>
                <button onClick={handleEndQuiz} className="sidebar-end-button">
                    End Assessment
                </button>
                <button onClick={handleLogout} className="sidebar-logout-button">
                    <LogOut className="inline-block w-4 h-4 mr-2" />
                    Logout
                </button>
            </div>

            <div className="progress-bar-container">
                <div className="progress-bar-wrapper">
                    <div className="progress-section">
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{ width: `${getProgressPercentage()}%` }}
                            />
                        </div>
                        <div className="progress-text">
                            {getProgressPercentage()}% Complete
                        </div>
                    </div>

                    <div className="timer-section">
                        <Clock className="timer-icon" />
                        <div className="timer-label">Time Left :</div>
                        <span className={`timer-text ${timeLeft <= 60 ? 'warning' : ''}`}>
              {formatTimeLeft()}
            </span>
                    </div>
                </div>
            </div>

            <main className="main-content">
                <div className="quiz-card">
                    <p className="question-text"><b>Q : </b>{currentQuestion.question}</p>
                    <p><b>Options</b></p><br/>
                    <div className="options-container">
                        {currentQuestion.answers.map((answer, index) => (
                            <button
                                key={index}
                                onClick={() => { if (!isSubmitting) handleAnswer(answer.option) }}
                                disabled={isSubmitting}
                                className={`option-button ${
                                    (attemptData?.responses?.find(r => r && r.ques_id === currentQuestion.ques_id)?.answer === answer.option)
                                        ? 'selected'
                                        : ''
                                }`}
                            >
                                {answer.option}
                            </button>
                        ))}
                    </div>
                </div>
            </main>

            <button onClick={handleEndQuiz} className="floating-end-button">
                End Assessment
            </button>

            {/* Remaining attempts dialog */}
            {showAttemptsDialog && (
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
                    <div style={{background:'#fff',borderRadius:12,padding:'20px 24px',maxWidth:420,width:'90%',boxShadow:'0 10px 30px rgba(0,0,0,0.2)'}}>
                        <h3 style={{margin:'0 0 10px',fontSize:18}}>Remaining Attempts</h3>
                        <p style={{margin:'0 0 16px',lineHeight:1.5}}>
                            You have <b>{remainingAttempts}</b> attempt{remainingAttempts !== 1 ? 's' : ''} remaining.
                        </p>
                        <div style={{display:'flex',justifyContent:'flex-end',gap:12}}>
                            <button
                                onClick={() => setShowAttemptsDialog(false)}
                                style={{padding:'8px 14px',borderRadius:8,border:'1px solid #ddd',background:'#fff',cursor:'pointer'}}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tab switch reminder dialog */}
            {showTabSwitchDialog && (
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
                    <div style={{background:'#fff',borderRadius:12,padding:'20px 24px',maxWidth:480,width:'90%',boxShadow:'0 10px 30px rgba(0,0,0,0.2)'}}>
                        <h3 style={{margin:'0 0 10px',fontSize:18}}>Stay on this tab</h3>
                        <p style={{margin:'0 0 8px',lineHeight:1.5}}>Please avoid switching tabs or reloading while your assessment is active.</p>
                        <p style={{margin:'0 0 16px',lineHeight:1.5}}>Remaining attempts: <b>{remainingAttempts}</b></p>
                        <div style={{display:'flex',justifyContent:'flex-end',gap:12}}>
                            <button
                                onClick={() => setShowTabSwitchDialog(false)}
                                style={{padding:'8px 14px',borderRadius:8,border:'1px solid #ddd',background:'#fff',cursor:'pointer'}}>
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error dialog */}
            {showErrorDialog && (
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1100}}>
                    <div style={{background:'#fff',borderRadius:12,padding:'20px 24px',maxWidth:480,width:'90%',boxShadow:'0 10px 30px rgba(0,0,0,0.2)'}}>
                        <h3 style={{margin:'0 0 10px',fontSize:18}}>Notice</h3>
                        <p style={{margin:'0 0 16px',lineHeight:1.5}}>{errorMessage}</p>
                        <div style={{display:'flex',justifyContent:'flex-end',gap:12}}>
                            <button onClick={() => setShowErrorDialog(false)} style={{padding:'8px 14px',borderRadius:8,border:'1px solid #ddd',background:'#fff',cursor:'pointer'}}>OK</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm end dialog */}
            {showConfirmEndDialog && (
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1100}}>
                    <div style={{background:'#fff',borderRadius:12,padding:'20px 24px',maxWidth:520,width:'90%',boxShadow:'0 10px 30px rgba(0,0,0,0.2)'}}>
                        <h3 style={{margin:'0 0 10px',fontSize:18}}>End Assessment?</h3>
                        <p style={{margin:'0 0 16px',lineHeight:1.5}}>{confirmEndMessage}</p>
                        <div style={{display:'flex',justifyContent:'flex-end',gap:12}}>
                            <button onClick={() => setShowConfirmEndDialog(false)} style={{padding:'8px 14px',borderRadius:8,border:'1px solid #ddd',background:'#fff',cursor:'pointer'}}>Cancel</button>
                            <button onClick={() => { setShowConfirmEndDialog(false); handleEndQuiz(false, true); }} style={{padding:'8px 14px',borderRadius:8,border:'1px solid #2563eb',background:'#2563eb',color:'#fff',cursor:'pointer'}}>Yes, End</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
